import React, { useContext, useMemo, useState } from "react";
import styles from "./mortgage-view.module.scss";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQuery } from "../../hooks/useQuery";
import { useHistory } from "react-router";
import ToastContext from "../../contexts/ToastContext";
import LoadingContext from "../../contexts/LoadingContext";
import {
  DocumentTypes,
  UploadDocMortSalaryBusinessReq,
} from "../../types/mortgage";
import {
  mortgageDocumentUploadApi,
  submitMortgageApplication,
} from "../../swr/mortgage";
import ModalLayout from "../../components/ModalLayout";
import SuccessModal from "../../components/Modal/success-modal";
import InputWithHeader from "../../components/Input/InputWithHeader";
import FileUploadInput from "../../components/Input/file-upload-input";
import PopUp from "../../components/Insurance/PopUp";

const fileSizeTest = (file: File | undefined) =>
  file ? file.size / (1024 * 1024) <= 10 : false;

const requiredFile = (msg: string) =>
  yup
    .mixed<File>()
    .required(msg)
    .test("fileSize", "File must be 10MB or less", fileSizeTest);

const optionalFile = () =>
  yup.mixed<File>().notRequired().default(undefined) as yup.MixedSchema<File | undefined>;

const buildSchema = (isBusinessApplicant: boolean, isDiaspora: boolean) =>
  yup.object({
    hasAccessAccount: yup.boolean().required("Please select an option"),

    accountNumber: yup
      .string()
      .defined()
      .when("hasAccessAccount", {
        is: true,
        then: (s) => s.min(1).required("Account number is required"),
        otherwise: (s) => s.default(""),
      }),

    meansOfIdentification: requiredFile("Means of Identification is required"),

    // Salary (Nigeria + Diaspora)
    letterOfEmployment: !isBusinessApplicant
      ? requiredFile("Letter of Employment is required")
      : optionalFile(),

    paySlip: !isBusinessApplicant
      ? requiredFile("Last 6 months Payslip is required")
      : optionalFile(),

    // Nigeria Salary only
    evidenceOfPayment: !isBusinessApplicant && !isDiaspora
      ? requiredFile("Evidence of Payment is required")
      : optionalFile(),

    // Nigeria (both) + Diaspora Business
    statementOfAccount: !(isDiaspora && !isBusinessApplicant)
      ? requiredFile("Statement of Bank Account is required")
      : optionalFile(),

    // Business only
    businessRegistration: isBusinessApplicant
      ? requiredFile("Business Registration Document is required")
      : optionalFile(),

    // Diaspora required
    proofOfResidence: isDiaspora
      ? requiredFile("Proof of Residence is required")
      : optionalFile(),

    taxEvidence: isDiaspora
      ? requiredFile("Tax Evidence is required")
      : optionalFile(),

    // Diaspora optional
    w2: optionalFile(),
    nationalInsurance: optionalFile(),
  });

const IntMortgageApp = () => {
  let query = useQuery();
  const history = useHistory();

  const { openError } = useContext(ToastContext);
  const { setLoading } = useContext(LoadingContext);

  const [submitSuccessful, setSubmitSuccessful] = useState(false);
  const isBusinessApplicant = query.get("epytppa") === "business";
  const isDiaspora = query.get("loc") === "1";
  const schema = useMemo(
    () => buildSchema(isBusinessApplicant, isDiaspora),
    [isBusinessApplicant, isDiaspora]
  );

  const [dropDownStates, setDropDownStates] = useState({
    hasAccessAccount: false,
  });
 
  const toggleDropdown = (key: keyof typeof dropDownStates, open: boolean) => {
    setDropDownStates((prev) => ({ ...prev, [key]: open }));
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<UploadDocMortSalaryBusinessReq>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      hasAccessAccount: undefined,
      accountNumber: undefined,
      meansOfIdentification: undefined,
      letterOfEmployment: undefined,
      paySlip: undefined,
      statementOfAccount: undefined,
      evidenceOfPayment: undefined,
      businessRegistration: undefined,
      proofOfResidence: undefined,
      taxEvidence: undefined,
      w2: undefined,
      nationalInsurance: undefined,
    },
  });

  const hasAccessAccount = watch("hasAccessAccount");

  const onSubmit = async (req: UploadDocMortSalaryBusinessReq) => {
    setLoading(true);
    try {
      const appId = query.get("dippa");
      const { hasAccessAccount, accountNumber, ...rest } = req;

      const entries = (Object.entries(rest) as [string, File][]).filter(
        ([, file]) => file != null 
      );
      
      await mortgageDocumentUploadApi({
        MortgageApplicationId: appId ? Number(appId) : 0,
        DocumentTypes: entries.map(
          ([doc]) =>
            DocumentTypes.find((type) => type.finder === doc)?.value ?? 0
        ),
        Files: entries.map(([, file]) => file),
      });

      const { data, error } = await submitMortgageApplication(
        appId ? Number(appId) : 0
      );

      if (data) {
        setSubmitSuccessful(true);
      }

      if (error) {
        openError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "An error occurred"
        );
      }
    } catch (error: any) {
      openError(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "An error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const clickOutSuccessModal = () => {
    setSubmitSuccessful(false);
    history.push("/mortgage-categories");
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.container}>
          <span className={styles.instruction}>
            Please fill the form below to begin your mortgage application
            process
          </span>

          <div className={styles.grid}>
            <Controller
              name="hasAccessAccount"
              control={control}
              render={({ field }) => (
                <InputWithHeader
                  header={
                    isBusinessApplicant
                      ? "Do you use Access Bank for your Business Account?"
                      : "Do you have an Access Bank Account?"
                  }
                  placeholder="Select"
                  type="dropdown"
                  value={
                    field.value === true
                      ? "Yes"
                      : field.value === false
                      ? "No"
                      : ""
                  }
                  onPressDropDown={() =>
                    toggleDropdown("hasAccessAccount", true)
                  }
                  error={errors.hasAccessAccount?.message}
                />
              )}
            />

            {hasAccessAccount ? (
              <Controller
                name="accountNumber"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header={
                      isBusinessApplicant
                        ? "Enter your Access Bank Business Account Number"
                        : "Access Bank Account Number"
                    }
                    placeholder="Enter Account Number"
                    type="string"
                    value={field.value || ""}
                    formKey="accountNumber"
                    handleChange={(_, val) => field.onChange(val)}
                    error={errors.accountNumber?.message}
                  />
                )}
              />
            ) : null}

            {/* Always required */}
            <Controller
              name="meansOfIdentification"
              control={control}
              render={({ field }) => (
                <FileUploadInput
                  header="Means of Identification (NIN, Voter’s Card or International Passport)"
                  placeholder="Upload Means of Identification"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.meansOfIdentification}
                />
              )}
            />

            {/* Diaspora required: Proof of Residence + Tax Evidence */}
            {isDiaspora && (
              <>
                <Controller
                  name="proofOfResidence"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Proof of Residence"
                      placeholder="Upload proof of residence"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.proofOfResidence}
                    />
                  )}
                />
                <Controller
                  name="taxEvidence"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Tax Evidence"
                      placeholder="Upload tax evidence"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.taxEvidence}
                    />
                  )}
                />
              </>
            )}

            {/* Salary: Letter of Employment + Payslip */}
            {!isBusinessApplicant && (
              <>
                <Controller
                  name="letterOfEmployment"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Letter of Employment"
                      placeholder="Upload letter of employment"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.letterOfEmployment}
                    />
                  )}
                />
                <Controller
                  name="paySlip"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Last 6 months Payslip"
                      placeholder="Upload 6 months payslip"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.paySlip}
                    />
                  )}
                />
              </>
            )}

            {/* Nigeria Salary only: Evidence of Payment */}
            {!isBusinessApplicant && !isDiaspora && (
              <Controller
                name="evidenceOfPayment"
                control={control}
                render={({ field }) => (
                  <FileUploadInput
                    header="Evidence of Additional Payment for the Past 12 Months"
                    placeholder="Upload evidence of payment"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.evidenceOfPayment}
                  />
                )}
              />
            )}

            {/* Bank Statement: Nigeria (both) + Diaspora Business */}
            {(!isDiaspora || isBusinessApplicant) && (
              <Controller
                name="statementOfAccount"
                control={control}
                render={({ field }) => (
                  <FileUploadInput
                    header="Statement of Bank Account for the Last 12 Months"
                    placeholder="Upload Bank Account Statement"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.statementOfAccount}
                  />
                )}
              />
            )}

            {/* Business only: Registration */}
            {isBusinessApplicant && (
              <Controller
                name="businessRegistration"
                control={control}
                render={({ field }) => (
                  <FileUploadInput
                    header="Business Registration Document"
                    placeholder="Upload business registration document"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.businessRegistration}
                  />
                )}
              />
            )}

            {/* Diaspora optional: W2 + National Insurance */}
            {isDiaspora && (
              <>
                <Controller
                  name="w2"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="W2 (Optional)"
                      placeholder="Upload W2 document"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.w2}
                    />
                  )}
                />
                <Controller
                  name="nationalInsurance"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="National Insurance (Optional)"
                      placeholder="Upload national insurance document"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.nationalInsurance}
                    />
                  )}
                />
              </>
            )}
          </div>

          <div className={styles["bottom-cont"]}>
            <div className={styles["back-but"]} role="button">
              <span>Back</span>
            </div>
            <button type="submit" className={styles["submit-btn"]}>
              Submit Application
            </button>
          </div>
        </div>
      </form>

      <ModalLayout
        modalOpen={dropDownStates.hasAccessAccount}
        onClickOut={() => toggleDropdown("hasAccessAccount", false)}
      >
        <PopUp
          options={["Yes", "No"]}
          click={(name) => {
            setValue("hasAccessAccount", name === "Yes", {
              shouldValidate: true,
            });
            toggleDropdown("hasAccessAccount", false);
          }}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={submitSuccessful}
        onClickOut={() => clickOutSuccessModal()}
      >
        <SuccessModal
          onClose={() => clickOutSuccessModal()}
          message="Your application has been submitted. We'll be in touch shortly."
        />
      </ModalLayout>
    </>
  );
};

export default IntMortgageApp;
